import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { apiWhatsappChannel, requestService } from '../../../../../Utils/Services';
import './RobotModeSelector.css';

export type RobotMode = 'link_cardapio' | 'ia' | 'ia_pedidos';
type Channel = 'baileys' | 'meta_cloud_api' | 'none';
const options: Array<{ value: RobotMode; label: string; description: string }> = [
  { value: 'link_cardapio', label: 'Enviar link do cardápio', description: 'Envia o link para o cliente fazer o pedido.' },
  { value: 'ia', label: 'Atender com IA', description: 'Responde sobre o restaurante e direciona para o cardápio.' },
  { value: 'ia_pedidos', label: 'Atender e receber pedidos com IA', description: 'Responde às dúvidas e monta o pedido na conversa. Envia para a loja após a confirmação do cliente.' },
];

export function savedRobotMode(company: any): RobotMode {
  return options.some(option => option.value === company?.modoAtendimentoRobo)
    ? company.modoAtendimentoRobo : company?.tipoRobo === 'ia' ? 'ia' : 'link_cardapio';
}

type Props = { empresaId: string; tipoRobo: string; value: RobotMode; disabled?: boolean; onChange: (mode: RobotMode) => void };

export default function RobotModeSelector({ empresaId, tipoRobo, value, disabled = false, onChange }: Props) {
  const history = useHistory();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setChannel(null); setCanEdit(false); setLoading(true); setError('');
    requestService(apiWhatsappChannel.url.replace('{id}', encodeURIComponent(empresaId)), {
      method: apiWhatsappChannel.method,
      headers: [['token', window.localStorage.getItem('token')]], credentials: 'include',
    }, history, (result: any) => {
      if (!active) return;
      const actual = result?.activeChannel || 'baileys';
      if (!['baileys', 'meta_cloud_api', 'none'].includes(actual) || typeof result?.canEdit !== 'boolean') {
        setError('Não foi possível verificar o canal do WhatsApp.'); setLoading(false); return;
      }
      setChannel(actual); setCanEdit(result.canEdit); setLoading(false);
    }, () => {
      if (active) { setError('Não foi possível verificar o canal do WhatsApp.'); setLoading(false); }
    });
    return () => { active = false; };
    // Load once for each company/opening; a status refresh must preserve the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, reload]);
  const hasAI = tipoRobo === 'ia';
  const supportsOrders = channel === 'baileys' || channel === 'meta_cloud_api';
  const dormant = (!hasAI && value !== 'link_cardapio') || (value === 'ia_pedidos' && channel !== null && !supportsOrders);
  return <div className="robot-mode-selector">
    <fieldset disabled={disabled || loading || !canEdit || tipoRobo === 'suporte'}>
      <legend>Como o robô deve atender?</legend>
      {options.map(option => <label key={option.value} className="robot-mode-option">
        <input type="radio" name="modoAtendimentoRobo" value={option.value} checked={value === option.value}
          disabled={(option.value !== 'link_cardapio' && !hasAI) || (option.value === 'ia_pedidos' && !supportsOrders)}
          onChange={() => onChange(option.value)} />
        <span><strong>{option.label}</strong><span>{option.description}</span></span>
      </label>)}
    </fieldset>
    {loading && <p role="status">Verificando canal do WhatsApp…</p>}
    {error && <p role="status">{error} <button type="button" onClick={() => setReload(current => current + 1)}>Tentar novamente</button></p>}
    {channel && <p>Canal atual: {channel === 'baileys' ? 'WhatsApp Web (QR Code)' : channel === 'meta_cloud_api' ? 'WhatsApp Oficial' : 'envios automáticos desativados'}.</p>}
    {!hasAI && <p>Os modos com IA requerem o módulo de atendimento com IA.</p>}
    {hasAI && channel === 'none' && <p>Ative um canal do WhatsApp para receber pedidos na conversa.</p>}
    {dormant && <p className="robot-mode-notice" role="status">Sua preferência foi preservada. {hasAI ? 'O recebimento de pedidos pela conversa fica inativo neste canal.' : 'O robô envia o link do cardápio enquanto o módulo de IA estiver inativo.'}</p>}
    {channel && !canEdit && <p>Você tem acesso somente para leitura.</p>}
  </div>;
}
